import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const REACT_APP_DEV_CHAT_REDIRECT_URL = process.env.REACT_APP_DEV_CHAT_REDIRECT_URL ?? ''


const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    justifyContent: 'center',
}));

const RowBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    justifyContent: 'center',
}));

const CardBox = styled(Box)(({ theme }) => ({
    flex: '1 1 250px',
    maxWidth: '250px',
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'transform 0.2s ease-in-out',
    minHeight: '180px',
    '&:hover': {
        transform: 'scale(1.02)',
    },
}));

const ImageBox = styled(Box)({
    width: '100%',
    height: '200px',
    marginBottom: '16px',
    borderRadius: '8px',
    overflow: 'hidden',
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
});

const WelcomePage = () => {
    const navigate = useNavigate();
    
    const mainPages = [
        {
            title: 'FieldOps Chatbot',
            description: 'FieldOps Chatbot for Agents',
            link: '/farm',
            image: 'https://source.unsplash.com/random/400x200?farm',
            color: '#4CAF50',
            useNewTab: false
        },
        {
            title: 'DataBot',
            description: 'DataBot for CNH Users',
            link: '/genie',
            image: 'https://source.unsplash.com/random/400x200?data',
            color: '#2196F3',
            useNewTab: false
        },
        // {
        //     title: 'Agent Assistant',
        //     description: 'AI Agent Assistant for Intelligent Conversations',
        //     link: '/job-assistant',
        //     image: 'https://source.unsplash.com/random/400x200?assistant',
        //     color: '#673AB7',
        //     useNewTab: false
        // },
        {
            title: 'QA assistant',
            description: 'QA Assistant for Racine Plant Group',
            link: '/transmission',
            image: 'https://source.unsplash.com/random/400x200?transmission',
            color: '#FF5722',
            useNewTab: false
        }
    ];

    const demoPages = [
        {
            title: 'FieldOps Chatbot',
            description: 'FieldOps Chatbot for Growers (Demo Only)',
            link: REACT_APP_DEV_CHAT_REDIRECT_URL + '/growers/?redirect=/growers/home',
            image: 'https://source.unsplash.com/random/400x200?magic',
            color: '#9C27B0',
            useNewTab: false
        },
        {
            title: 'Field Boundary Autogen',
            description: '(Demo Only)',
            link: '/map',
            image: 'https://source.unsplash.com/random/400x200?map',
            color: '#FF9800',
            useNewTab: false
        },
        {
            title: 'FieldOps Agent',
            description: 'FieldOps AI Agent PoC for Growers (Demo Only)',
            link: REACT_APP_DEV_CHAT_REDIRECT_URL + '/agent/?redirect=/agent/home',
            image: 'https://source.unsplash.com/random/400x200?map',
            color: '#d30ab8',
            useNewTab: false
        }
    ];

    const handleNavigation = (link: string, useNewTab: boolean) => {
        // Check if it's an external URL (starts with http:// or https://)
        const isExternalUrl = link.startsWith('http://') || link.startsWith('https://');
        
        if (useNewTab) {
            window.open(link, '_blank');
        } else if (isExternalUrl) {
            // For external URLs, use window.location to redirect
            window.location.href = link;
        } else {
            // For internal routes, use React Router navigate
            navigate(link);
        }
    };

    const renderPageCard = (page: any, index: number) => (
        <CardBox 
            key={page.title+'-'+index}
            sx={{ 
                borderTop: `4px solid ${page.color}`,
            }}
        >
            {/* <ImageBox>
                <img src={page.image} alt={page.title} />
            </ImageBox> */}
            <Typography 
                variant="h6" 
                sx={{ 
                    mb: 0.5,
                    color: page.color,
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    textAlign: 'center'
                }}
            >
                {page.title}
            </Typography>
            <Typography 
                variant="caption" 
                sx={{ 
                    mb: 1,
                    textAlign: 'center',
                    color: 'text.secondary',
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.9rem',
                    lineHeight: 1.3
                }}
            >
                {page.description}
            </Typography>
            <Button 
                variant="contained" 
                size="small"
                onClick={() => handleNavigation(page.link, page.useNewTab)}
                sx={{ 
                    backgroundColor: page.color,
                    fontSize: '0.9rem',
                    padding: '6px 12px',
                    '&:hover': {
                        backgroundColor: page.color,
                        opacity: 0.9
                    },
                    width: '100%'
                }}
            >
                Launch
            </Button>
        </CardBox>
    );

    return (
        <Box sx={{ 
            minHeight: '60vh', 
            width: '80%',
            bgcolor: 'background.default', 
            py: 1,
            position: 'relative',
            zIndex: 1,
            mt: 3, // Add margin top to account for header
            mx: 'auto'
        }}>
            <StyledBox>
                {/* Main Pages Row */}
                <RowBox>
                    {mainPages.map((page, index) => renderPageCard(page, index))}
                </RowBox>
                
                {/* Demo Pages Row */}
                <RowBox>
                    {demoPages.map((page, index) => renderPageCard(page, index))}
                </RowBox>
            </StyledBox>
        </Box>
    );
};

export default WelcomePage;
